import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { AsyncPipe } from '@angular/common';
import { NxButtonModule } from '@aposin/ng-aquila/button';
import { NxFormfieldModule } from '@aposin/ng-aquila/formfield';
import { NxIconModule } from '@aposin/ng-aquila/icon';
import { NxInputModule } from '@aposin/ng-aquila/input';
import { NxMessageModule, NxMessageToastService } from '@aposin/ng-aquila/message';
import { NxSpinnerModule } from '@aposin/ng-aquila/spinner';
import { NxTaglistModule } from '@aposin/ng-aquila/taglist';
import { catchError, take, throwError } from 'rxjs';
import { Application, CreateApplicationDto, UpdateApplicationDto } from '../../../models/application.model';
import { HttpError } from '../../../models/http-error.model';
import { ApplicationService } from '../../../services/application.service';
import { MockDataService } from '../../../services/mock-data.service';
import { PermissionService } from '../../../services/permission.service';
import {
  addEmailToDistributions as addEmailToDistributionsUtil,
  isValidEmail,
  removeEmailFromDistributions as removeEmailFromDistributionsUtil
} from '../../../utils/email-distribution.utils';
import { isLocalEnvironment } from '../../../utils/environment.utils';

@Component({
  selector: 'app-application-form',
  standalone: true,
  imports: [
    NxFormfieldModule,
    NxTaglistModule,
    NxIconModule,
    NxInputModule,
    NxButtonModule,
    ReactiveFormsModule,
    NxSpinnerModule,
    NxMessageModule,
    AsyncPipe,
  ],
  templateUrl: './application-form.component.html',
  styleUrl: './application-form.component.scss'
})
export class ApplicationFormComponent implements OnChanges, OnInit {
  private applicationService = inject(ApplicationService);
  private mockDataService = inject(MockDataService);
  private formBuilder = inject(FormBuilder);
  private messageToastService = inject(NxMessageToastService);
  permissionService = inject(PermissionService);

  @Input() pagerDutyServiceId: string | null = null;
  @Input() applicationId: string | null = null;
  @Output() closePanel = new EventEmitter<{ refreshList: boolean; updatedApplication?: Application }>();

  isApplicationLoading = false;
  isApplicationUpdating = false;
  applicationForm: FormGroup | null = null;
  emailDistributions: string[] = [];
  responseError: HttpError | null = null;

  get isCreateMode(): boolean {
    return !this.applicationId;
  }

  ngOnChanges(): void {
    if (this.isCreateMode) {
      if (!this.applicationForm) {
        this.initForm();
      }
      this.emailDistributions = [];
      this.applicationForm?.patchValue({
        applicationName: '',
        contactEmail: '',
        emailDistributions: []
      });
    } else {
      this.initData();
    }
  }

  ngOnInit(): void {
    this.initForm();
  }

  initForm() {
    this.applicationForm = this.formBuilder.group({
      applicationId: [{ value: null, disabled: true }],
      applicationName: ['', Validators.required],
      contactEmail: ['', [Validators.required, Validators.email]],
      emailDistributions: [[]]
    });
  }

  addEmailToDistributions(value: string, inputElement: HTMLInputElement) {
    this.emailDistributions = addEmailToDistributionsUtil(value, inputElement, this.emailDistributions);
    this.applicationForm?.patchValue({
      emailDistributions: this.emailDistributions
    });
  }

  removeEmailFromDistributions(email: string) {
    this.emailDistributions = removeEmailFromDistributionsUtil(email, this.emailDistributions);
    this.applicationForm?.patchValue({
      emailDistributions: this.emailDistributions
    });
  }

  initData() {
    if (!this.pagerDutyServiceId || !this.applicationId) {
      return;
    }

    this.responseError = null;
    this.emailDistributions = [];
    this.isApplicationLoading = true;

    const dataService = isLocalEnvironment()
      ? this.mockDataService.getApplicationById(this.pagerDutyServiceId, this.applicationId)
      : this.applicationService.getApplicationById(this.pagerDutyServiceId, this.applicationId);

    dataService
      .pipe(
        take(1),
        catchError((error) => this.handleError('Failed to load application', 'loadApplication', error)),
      )
      .subscribe((response) => {
        const application = response?.body || null;
        this.isApplicationLoading = false;

        this.emailDistributions = application?.emailDistributions ?? [];
        this.applicationForm?.patchValue({
          applicationId: application?.applicationId,
          applicationName: application?.applicationName,
          contactEmail: application?.contactEmail,
        });
      });
  }

  updateApplication(inputElement?: HTMLInputElement) {
    if (!this.pagerDutyServiceId || !this.applicationForm || !this.applicationForm.valid) {
      return;
    }

    if (inputElement && inputElement.value.trim()) {
      const trimmedEmail = inputElement.value.trim();

      if (isValidEmail(trimmedEmail)) {
        if (!this.emailDistributions.includes(trimmedEmail)) {
          this.emailDistributions = [...this.emailDistributions, trimmedEmail];
        }
      }

      inputElement.value = '';
    }

    this.isApplicationUpdating = true;
    this.responseError = null;

    if (this.isCreateMode) {
      this.createApplication();
    } else {
      this.updateExistingApplication();
    }
  }

  createApplication() {
    if (!this.pagerDutyServiceId || !this.applicationForm) {
      return;
    }

    const formValue = this.applicationForm.value;
    const createDto: CreateApplicationDto = {
      applicationName: formValue.applicationName,
      contactEmail: formValue.contactEmail,
      emailDistributions: this.emailDistributions || [],
      features: []
    };

    const dataService = isLocalEnvironment()
      ? this.mockDataService.createApplication(this.pagerDutyServiceId, createDto)
      : this.applicationService.createApplication(this.pagerDutyServiceId, createDto);

    dataService
      .pipe(
        take(1),
        catchError((error) => this.handleError('Failed to create application', 'createApplication', error)),
      )
      .subscribe((response) => {
        this.isApplicationUpdating = false;
        this.messageToastService.open('Application created successfully', {
          context: 'success',
          duration: 5000,
        });
        this.onCloseForm(true, response?.body || undefined);
      });
  }

  updateExistingApplication() {
    if (!this.pagerDutyServiceId || !this.applicationId || !this.applicationForm) {
      return;
    }

    const applicationDto: UpdateApplicationDto = {
      applicationName: this.applicationForm.value.applicationName,
      contactEmail: this.applicationForm.value.contactEmail,
      emailDistributions: this.emailDistributions
    };

    const dataService = isLocalEnvironment()
      ? this.mockDataService.updateApplication(this.pagerDutyServiceId, this.applicationId, applicationDto)
      : this.applicationService.updateApplication(this.pagerDutyServiceId, this.applicationId, applicationDto);

    dataService
      .pipe(
        take(1),
        catchError((error) => this.handleError('Failed to update application', 'updateApplication', error)),
      )
      .subscribe((response) => {
        this.isApplicationUpdating = false;
        this.messageToastService.open('Application updated successfully', {
          context: 'success',
          duration: 5000,
        });
        this.onCloseForm(true, response?.body || undefined);
      });
  }

  onCloseForm(refreshList = false, updatedApplication?: Application) {
    this.applicationForm?.reset();
    this.emailDistributions = [];
    this.closePanel.emit({ refreshList, updatedApplication });
  }

  handleError(error: string, errorType: string, errorDetails: HttpErrorResponse) {
    this.isApplicationLoading = false;
    this.isApplicationUpdating = false;

    this.responseError = {
      error,
      errorType,
      details: null
    };

    if (errorDetails?.error?.message) {
      this.responseError.details = errorDetails.error.message;
    }

    return throwError(() => error);
  }
}
